/**
 * Compact narrative summary card for the plan overview (DEP-026).
 *
 * Replaces the generic "Narrative record → catalog" Related card when the
 * plan_narrative module is on: shows the cycle's linked narrative record
 * (title, status, version, last updated) and links to the plan's narrative
 * subpage. Self-contained — fetches its own row so PlanningPlanDetail's diff
 * stays one swapped card.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { getPorForCycle } from '../../lib/planOfRecordService';
import type { PorRecord } from '../../types/plan-of-record';
import { planNarrativeBasePath } from '../../lib/prioritizationPlanInstrumentPath';
import { PorStatusPill, formatDate } from '../plan-of-record/porUi';

export function PlanNarrativeSummaryCard({ cycleId }: { cycleId: string }) {
  const [record, setRecord] = useState<PorRecord | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void getPorForCycle(cycleId).then(({ data }) => {
      if (!cancelled) setRecord(data);
    });
    return () => {
      cancelled = true;
    };
  }, [cycleId]);

  return (
    <Link
      to={planNarrativeBasePath(cycleId)}
      className="group flex items-start gap-3 border border-[var(--rule)] px-4 py-3 hover:border-primary-200 hover:bg-primary-50/30 transition-all"
    >
      <BookOpen className="h-4 w-4 text-[var(--text-ter)] group-hover:text-primary-500 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-medium text-[var(--text-pri)] truncate">
            {record === undefined ? 'Narrative' : record ? record.title : 'Narrative'}
          </p>
          {record && <PorStatusPill status={record.status} />}
        </div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          {record === undefined
            ? 'Loading…'
            : record
              ? `v${record.version} · updated ${formatDate(record.last_updated)} · frozen with POR authorization`
              : 'No narrative record yet — create the written story behind this plan'}
        </p>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-primary-400 shrink-0 mt-0.5" />
    </Link>
  );
}
